import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Divider,
  useMediaQuery,
  Snackbar,
  Alert,
  ListSubheader,
} from '@mui/material';
import {
  ViewColumn,
  ViewAgenda,
  ViewWeek,
  Swipe,
  Add,
  Save,
  Print,
  Share,
  FolderOpen,
  CompareArrows,
  Close,
  Edit,
  Delete,
  MoreVert,
  QrCode2,
} from '@mui/icons-material';
import scheduleConfig from './scheduleConfig.json';
import { AppState, FilterState, SubjectData, CustomTimeBlock, SavedSchedule, Course } from './types';
import FilterPanel from './components/FilterPanel';
import ScheduleGrid from './components/ScheduleGrid';
import OnlineCoursesList from './components/OnlineCoursesList';
import ImportModal from './components/ImportModal';
import SaveLoadModal from './components/SaveLoadModal';
import CustomBlockModal from './components/CustomBlockModal';
import ShareAppModal from './components/ShareAppModal';
import { encodeCustomBlockForShare, decodeCustomBlockFromShare, formatFetchedAt, fetchScheduleMeta, fetchScheduleSnapshot, hydrateSnapshot, rematchSavedCourses, markUnavailable, countNewlyUnavailable, isTermChange, catalogIsStale, ScheduleSnapshot } from './utils/parser';
import { occupiedMeetings, conflictingCrns, firstConflictName } from './utils/conflicts';
import { filtersFromNeededCourses, matchesNeededOrSubjectFilters, neededFromLegacyCourseFilter } from './utils/catalogClasses';

declare global {
  interface Window {
    refreshCatalog?: () => Promise<void>;
  }
}

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb',
    },
    secondary: {
      main: '#059669',
    },
    background: {
      default: '#ffffff',
      paper: '#f6f8fa',
    },
    text: {
      primary: '#0b0f14',
      secondary: '#4b5563',
    },
  },
  typography: {
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#f0f4f8',
          border: '1px solid #d1d5db',
          color: '#0b0f14',
          borderRadius: '999px',
          fontSize: '12px',
          padding: '6px 8px',
          '&:hover': {
            backgroundColor: '#e5e7eb',
          },
          '&.MuiChip-clickable:active': {
            backgroundColor: '#e5e7eb',
          },
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#8ab4f8',
    },
    secondary: {
      main: '#80eec0',
    },
    background: {
      default: '#0b0f14',
      paper: '#121821',
    },
    text: {
      primary: '#e6edf3',
      secondary: '#9fb3c8',
    },
  },
  typography: {
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#0f1824',
          border: '1px solid #233146',
          color: '#e6edf3',
          borderRadius: '999px',
          fontSize: '12px',
          padding: '6px 8px',
          '&:hover': {
            backgroundColor: '#1a2532',
          },
          '&.MuiChip-clickable:active': {
            backgroundColor: '#1a2532',
          },
        },
      },
    },
  },
});

const initialFilterState: FilterState = {
  subjectAllow: new Set(),
  courseAllow: new Set(),
  instructorAllow: new Set(),
  campusAllow: new Set(),
  showOnline: false,
  showFullClasses: false,
  showFullWaitlist: false,
  showConflicts: false,
  neededCourses: new Set(),
};

const initialAppState: AppState = {
  allCourses: [],
  onlineCourses: [],
  mySchedule: [],
  myOnlineClasses: [],
  customBlocks: [],
  subjects: new Set(),
  courses: new Set(),
  instructors: new Set(),
  campuses: new Set(),
  subjectData: new Map(),
  filters: initialFilterState,
  isLoading: false,
  error: null,
  importProgress: 0,
  importProgressText: '',
  catalogFetchedAt: null,
  catalogTermCode: null,
  catalogTermLabel: null,
};

const LEGACY_TERM_LABEL = `${scheduleConfig.term} ${scheduleConfig.year}`;

function buildCatalogFields(courses: Course[], online: Course[]) {
  const subjectData = new Map<string, SubjectData>();
  const allForFilters = [...courses, ...online];
  for (const course of allForFilters) {
    if (!subjectData.has(course.Subject)) {
      subjectData.set(course.Subject, {
        courses: [],
        courseNumbers: new Set(),
        instructors: new Set(),
        campuses: new Set(),
      });
    }
    const subjectInfo = subjectData.get(course.Subject)!;
    subjectInfo.courses.push(course);
    subjectInfo.courseNumbers.add(course.Course);
    if (course.Instructor) subjectInfo.instructors.add(course.Instructor);
    if (course.Campus) subjectInfo.campuses.add(course.Campus);
  }
  return {
    allCourses: courses,
    onlineCourses: online,
    subjects: new Set(allForFilters.map(c => c.Subject)),
    courses: new Set(allForFilters.map(c => c.Course)),
    instructors: new Set(allForFilters.map(c => c.Instructor).filter(Boolean)),
    campuses: new Set(allForFilters.map(c => c.Campus).filter(Boolean)),
    subjectData,
  };
}

function emptyFilters(): FilterState {
  return {
    subjectAllow: new Set(),
    courseAllow: new Set(),
    instructorAllow: new Set(),
    campusAllow: new Set(),
    showOnline: false,
    showFullClasses: false,
    showFullWaitlist: false,
    showConflicts: false,
    neededCourses: new Set(),
  };
}

type LoadedLocalData = {
  allCourses: Course[];
  onlineCourses: Course[];
  mySchedule: Course[];
  myOnlineClasses: Course[];
  subjects: Set<string>;
  courses: Set<string>;
  instructors: Set<string>;
  campuses: Set<string>;
  subjectData: Map<string, SubjectData>;
  filters: FilterState;
  customBlocks: CustomTimeBlock[];
  isLightMode: boolean;
  catalogFetchedAt: string | null;
  catalogTermCode: string | null;
  catalogTermLabel: string | null;
};

type CatalogSyncOptions = {
  mySchedule?: Course[];
  myOnlineClasses?: Course[];
  customBlocks?: CustomTimeBlock[];
  localTermCode?: string | null;
  localTermLabel?: string | null;
  localCourses?: Course[];
};

function hasRestorableSession(data: {
  mySchedule?: unknown[];
  myOnlineClasses?: unknown[];
  customBlocks?: unknown[];
  filters?: { subjectAllow?: Set<string>; neededCourses?: Set<string> };
}): boolean {
  return (data.mySchedule?.length || 0) > 0
    || (data.myOnlineClasses?.length || 0) > 0
    || (data.customBlocks?.length || 0) > 0
    || (data.filters?.subjectAllow?.size || 0) > 0
    || (data.filters?.neededCourses?.size || 0) > 0;
}

function App() {
  const [appState, setAppState] = useState<AppState>(initialAppState);
  const [isLightMode, setIsLightMode] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [saveLoadModalOpen, setSaveLoadModalOpen] = useState(false);
  const [customBlockModalOpen, setCustomBlockModalOpen] = useState(false);
  const [editingCustomBlock, setEditingCustomBlock] = useState<CustomTimeBlock | null>(null);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [courseOpacity, setCourseOpacity] = useState(0.6);
  const [showOpacityMenu, setShowOpacityMenu] = useState(false);
  const [scheduleLayout, setScheduleLayout] = useState<'horizontal' | 'vertical'>('horizontal');
  const [weekView, setWeekView] = useState<'full' | 'twoDay'>(() =>
    typeof window !== 'undefined' && window.innerWidth <= 900 ? 'twoDay' : 'full'
  );
  const [layoutMenuAnchor, setLayoutMenuAnchor] = useState<{ available: HTMLElement | null; mySchedule: HTMLElement | null }>({ available: null, mySchedule: null });
  const [savedSchedules, setSavedSchedules] = useState<SavedSchedule[]>([]);
  const [catalogNotice, setCatalogNotice] = useState<string | null>(null);
  const [shareAppOpen, setShareAppOpen] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | ''>('');
  const [saveLoadMenuAnchor, setSaveLoadMenuAnchor] = useState<HTMLElement | null>(null);
  const [saveScheduleModalOpen, setSaveScheduleModalOpen] = useState(false);
  const [scheduleName, setScheduleName] = useState('');
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [editScheduleModalOpen, setEditScheduleModalOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareSchedule1Id, setCompareSchedule1Id] = useState<string | ''>('');
  const [compareSchedule2Id, setCompareSchedule2Id] = useState<string | ''>('');
  const [compareMenuAnchor, setCompareMenuAnchor] = useState<HTMLElement | null>(null);
  const [myScheduleActionsAnchor, setMyScheduleActionsAnchor] = useState<HTMLElement | null>(null);
  const isNarrowScheduleHeader = useMediaQuery('(max-width:900px)');
  const syncingCatalogRef = React.useRef(false);
  const [catalogMeta, setCatalogMeta] = useState({
    year: scheduleConfig.year as number,
    term: scheduleConfig.term as string,
    termCode: (scheduleConfig as { termCode?: string }).termCode || '',
  });

  const scheduleLabel = `${catalogMeta.term} ${catalogMeta.year}`;
  const lastUpdatedLabel = formatFetchedAt(appState.catalogFetchedAt);

  // Update CSS custom property when courseOpacity changes
  useEffect(() => {
    document.documentElement.style.setProperty('--course-opacity', courseOpacity.toString());
  }, [courseOpacity]);

  // Calculate shared time range for both schedules based on filtered courses
  const sharedTimeRange = useMemo(() => {
    // Check if any filter chips are selected (not including showFullClasses/showFullWaitlist)
    const hasFilterChips = appState.filters.neededCourses.size > 0 ||
                          appState.filters.subjectAllow.size > 0 || 
                          appState.filters.instructorAllow.size > 0 || 
                          appState.filters.campusAllow.size > 0;
    
    let coursesToConsider;
    
    if (hasFilterChips) {
      // Filter chips are selected - filter available courses based on current filters
      const occupied = occupiedMeetings(appState.mySchedule, appState.customBlocks);
      const conflictSet = conflictingCrns(appState.allCourses, occupied);
      const filteredAvailableCourses = appState.allCourses.filter(course => {
        const subjOk = matchesNeededOrSubjectFilters(course, appState.filters);
        const instrOk = appState.filters.instructorAllow.size === 0 || appState.filters.instructorAllow.has(course.Instructor);
        const campusOk = appState.filters.campusAllow.size === 0 || appState.filters.campusAllow.has(course.Campus);
        
        const isFull = course.Remaining <= 0 && course.Capacity > 0;
        const fullOk = appState.filters.showFullClasses || !isFull;
        const isWaitlistFull = course.WaitRem <= 0 && course.WaitCap > 0;
        const waitlistOk = appState.filters.showFullWaitlist || !isWaitlistFull;
        const conflictOk = appState.filters.showConflicts || !conflictSet.has(course.CRN);
        
        return subjOk && instrOk && campusOk && fullOk && waitlistOk && conflictOk;
      });
      
      // Combine filtered available courses with student schedule
      coursesToConsider = [...filteredAvailableCourses, ...appState.mySchedule];
    } else {
      // No filter chips selected - use student's selected courses only
      coursesToConsider = appState.mySchedule;
    }
    
    if (coursesToConsider.length === 0) {
      return { startMin: 8 * 60, endMin: 18 * 60 }; // Default 8 AM to 6 PM
    }
    
    const allStartMins = coursesToConsider.map(course => course.StartMin);
    const allEndMins = coursesToConsider.map(course => course.EndMin);
    
    const earliestStart = Math.min(...allStartMins);
    const latestEnd = Math.max(...allEndMins);
    
    if (hasFilterChips) {
      // When filter chips are active, round to nearest hour and ensure minimum range
      const startMin = Math.floor(earliestStart / 60) * 60;
      const endMin = Math.ceil(latestEnd / 60) * 60;
      
      // Ensure minimum range of 8 AM to 6 PM
      const minStart = 8 * 60; // 8 AM
      const minEnd = 18 * 60; // 6 PM
      
      return {
        startMin: Math.min(startMin, minStart),
        endMin: Math.max(endMin, minEnd)
      };
    } else {
      // No filter chips selected - use student's courses with 1 hour buffer
      const startMin = Math.max(0, earliestStart - 60); // 1 hour before first class
      const endMin = latestEnd + 60; // 1 hour after last class
      
      // Round to nearest hour for cleaner display
      const roundedStartMin = Math.floor(startMin / 60) * 60;
      const roundedEndMin = Math.ceil(endMin / 60) * 60;
      
      return {
        startMin: roundedStartMin,
        endMin: roundedEndMin
      };
    }
  }, [appState.allCourses, appState.mySchedule, appState.customBlocks, appState.filters]);

  // Calculate total units for display
  const calculateTotalUnits = useCallback(() => {
    let totalUnits = 0;
    appState.mySchedule.forEach(course => {
      if (course.Units > 0 && !course.unavailable) totalUnits += course.Units;
    });
    appState.myOnlineClasses.forEach(course => {
      if (course.Units > 0 && !course.unavailable) totalUnits += course.Units;
    });
    return totalUnits;
  }, [appState.mySchedule, appState.myOnlineClasses]);

  const totalUnits = calculateTotalUnits();

  // Local storage functions
  const saveToLocalStorage = useCallback(() => {
    try {
      const dataToSave = {
        allCourses: appState.allCourses,
        onlineCourses: appState.onlineCourses,
        mySchedule: appState.mySchedule,
        myOnlineClasses: appState.myOnlineClasses,
        subjects: Array.from(appState.subjects),
        courses: Array.from(appState.courses),
        instructors: Array.from(appState.instructors),
        campuses: Array.from(appState.campuses),
        subjectData: Array.from(appState.subjectData.entries()).map(([key, value]) => [
          key,
          {
            courses: value.courses,
            courseNumbers: Array.from(value.courseNumbers),
            instructors: Array.from(value.instructors),
            campuses: Array.from(value.campuses),
          }
        ]),
        filters: {
          subjectAllow: Array.from(appState.filters.subjectAllow),
          courseAllow: Array.from(appState.filters.courseAllow),
          instructorAllow: Array.from(appState.filters.instructorAllow),
          campusAllow: Array.from(appState.filters.campusAllow),
          showOnline: appState.filters.showOnline,
          showFullClasses: appState.filters.showFullClasses,
          showFullWaitlist: appState.filters.showFullWaitlist,
          showConflicts: appState.filters.showConflicts,
          neededCourses: Array.from(appState.filters.neededCourses),
        },
        customBlocks: appState.customBlocks,
        isLightMode,
        catalogFetchedAt: appState.catalogFetchedAt,
        catalogTermCode: appState.catalogTermCode,
        catalogTermLabel: appState.catalogTermLabel,
        timestamp: Date.now(),
      };
      localStorage.setItem('ssb_data', JSON.stringify(dataToSave));
      console.log('💾 Data saved to local storage');
      console.log('💾 Custom blocks saved:', dataToSave.customBlocks?.length || 0);
    } catch (error) {
      console.error('Failed to save to local storage:', error);
    }
  }, [appState, isLightMode]);

  const loadFromLocalStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem('ssb_data');
      console.log('🔍 Raw saved data exists:', !!saved);
      if (saved) {
        const data = JSON.parse(saved);
        console.log('🔍 Parsed data keys:', Object.keys(data));
        console.log('🔍 Data timestamp:', data.timestamp);
        console.log('🔍 All courses length:', data.allCourses?.length);
        console.log('🔍 Custom blocks length:', data.customBlocks?.length);

        if (data.allCourses && data.allCourses.length > 0) {
          const subjectAllow = new Set<string>(data.filters?.subjectAllow || []);
          let neededCourses = new Set<string>(data.filters?.neededCourses || []);
          if (neededCourses.size === 0) {
            neededCourses = neededFromLegacyCourseFilter(
              subjectAllow,
              new Set<string>(data.filters?.courseAllow || []),
              [...data.allCourses, ...(data.onlineCourses || [])],
            );
          }
          const loaded: LoadedLocalData = {
            allCourses: data.allCourses,
            onlineCourses: data.onlineCourses || [],
            mySchedule: data.mySchedule || [],
            myOnlineClasses: data.myOnlineClasses || [],
            subjects: new Set<string>(data.subjects || []),
            courses: new Set<string>(data.courses || []),
            instructors: new Set<string>(data.instructors || []),
            campuses: new Set<string>(data.campuses || []),
            subjectData: new Map(
              (data.subjectData || []).map(([key, value]: [string, any]) => [
                key,
                {
                  courses: value.courses || [],
                  courseNumbers: new Set(Array.isArray(value.courseNumbers) ? value.courseNumbers : []),
                  instructors: new Set(Array.isArray(value.instructors) ? value.instructors : []),
                  campuses: new Set(Array.isArray(value.campuses) ? value.campuses : []),
                }
              ])
            ),
            filters: {
              subjectAllow,
              courseAllow: new Set<string>(),
              instructorAllow: new Set<string>(data.filters?.instructorAllow || []),
              campusAllow: new Set<string>(data.filters?.campusAllow || []),
              showOnline: data.filters?.showOnline || false,
              showFullClasses: data.filters?.showFullClasses || false,
              showFullWaitlist: data.filters?.showFullWaitlist || false,
              showConflicts: data.filters?.showConflicts || false,
              neededCourses,
            },
            customBlocks: data.customBlocks || [],
            isLightMode: data.isLightMode || false,
            catalogFetchedAt: data.catalogFetchedAt || null,
            catalogTermCode: data.catalogTermCode || null,
            catalogTermLabel: data.catalogTermLabel || null,
          };
          return loaded;
        }
      }
    } catch (error) {
      console.error('Failed to load from local storage:', error);
    }
    return null;
  }, []);

  const clearLocalStorage = useCallback(() => {
    try {
      localStorage.removeItem('ssb_data');
      console.log('🗑️ Local storage cleared');
    } catch (error) {
      console.error('Failed to clear local storage:', error);
    }
  }, []);

  const persistCatalogOnly = useCallback((savedData: LoadedLocalData) => {
    try {
      const dataToSave = {
        allCourses: savedData.allCourses,
        onlineCourses: savedData.onlineCourses,
        mySchedule: [],
        myOnlineClasses: [],
        subjects: Array.from(savedData.subjects),
        courses: Array.from(savedData.courses),
        instructors: Array.from(savedData.instructors),
        campuses: Array.from(savedData.campuses),
        subjectData: Array.from(savedData.subjectData.entries()).map(([key, value]) => [
          key,
          {
            courses: value.courses,
            courseNumbers: Array.from(value.courseNumbers),
            instructors: Array.from(value.instructors),
            campuses: Array.from(value.campuses),
          }
        ]),
        filters: {
          subjectAllow: [],
          courseAllow: [],
          instructorAllow: [],
          campusAllow: [],
          showOnline: false,
          showFullClasses: false,
          showFullWaitlist: false,
          showConflicts: false,
          neededCourses: [],
        },
        customBlocks: [],
        isLightMode: savedData.isLightMode,
        catalogFetchedAt: savedData.catalogFetchedAt,
        catalogTermCode: savedData.catalogTermCode,
        catalogTermLabel: savedData.catalogTermLabel,
        timestamp: Date.now(),
      };
      localStorage.setItem('ssb_data', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Failed to keep local catalog:', error);
    }
  }, []);

  // Auto-save effect
  React.useEffect(() => {
    if (appState.allCourses.length > 0) {
      const timeoutId = setTimeout(saveToLocalStorage, 1000); // Debounce saves
      return () => clearTimeout(timeoutId);
    }
  }, [appState, saveToLocalStorage]);

  const applySnapshot = useCallback((snapshot: ScheduleSnapshot, options: CatalogSyncOptions = {}) => {
    const { courses, online } = hydrateSnapshot(snapshot);
    const catalogFields = buildCatalogFields(courses, online);
    const newTermLabel = `${snapshot.term} ${snapshot.year}`;
    const savedClasses = options.mySchedule ?? [];
    const savedOnline = options.myOnlineClasses ?? [];
    const termChanged = isTermChange(options.localTermCode, options.localCourses, snapshot.termCode, courses);

    let mySchedule: Course[];
    let myOnlineClasses: Course[];
    if (termChanged) {
      // CRNs are reused between semesters, so old picks are archived instead of re-matched.
      if ([...savedClasses, ...savedOnline].some(course => !course.isCustomBlock)) {
        const fallbackLabel = LEGACY_TERM_LABEL === newTermLabel ? 'Previous semester' : LEGACY_TERM_LABEL;
        const oldTermLabel = options.localTermLabel || fallbackLabel;
        const archiveName = `${oldTermLabel} schedule`;
        const now = Date.now();
        setSavedSchedules(prev => [...prev, {
          id: `schedule_${now}`,
          name: archiveName,
          mySchedule: markUnavailable(savedClasses),
          myOnlineClasses: markUnavailable(savedOnline),
          customBlocks: options.customBlocks ?? [],
          termCode: options.localTermCode || undefined,
          termLabel: oldTermLabel,
          createdAt: now,
          updatedAt: now,
        }]);
        setCatalogNotice(`The ${newTermLabel} schedule is here. Your ${oldTermLabel} classes were saved as "${archiveName}" in your saved schedules.`);
      }
      mySchedule = [];
      myOnlineClasses = [];
    } else {
      mySchedule = rematchSavedCourses(savedClasses, courses);
      myOnlineClasses = rematchSavedCourses(savedOnline, online);
      const lost = countNewlyUnavailable(savedClasses, mySchedule) + countNewlyUnavailable(savedOnline, myOnlineClasses);
      if (lost > 0) {
        setCatalogNotice(`${lost} ${lost === 1 ? 'class' : 'classes'} in My Schedule ${lost === 1 ? 'is' : 'are'} no longer listed by COS. ${lost === 1 ? "It's" : "They're"} still shown as "No longer listed" so you can pick a replacement.`);
      }
    }

    setCatalogMeta({
      year: snapshot.year,
      term: snapshot.term,
      termCode: snapshot.termCode || '',
    });
    setAppState(prev => ({
      ...prev,
      ...catalogFields,
      mySchedule,
      myOnlineClasses,
      customBlocks: options.customBlocks ?? prev.customBlocks,
      catalogFetchedAt: snapshot.fetchedAt,
      catalogTermCode: snapshot.termCode || null,
      catalogTermLabel: newTermLabel,
      isLoading: false,
      error: null,
      importProgress: 100,
      importProgressText: 'Schedule loaded',
    }));
    setTimeout(() => {
      setAppState(prev => ({
        ...prev,
        importProgress: 0,
        importProgressText: '',
      }));
    }, 800);
  }, []);

  const syncCatalog = useCallback(async (options: CatalogSyncOptions & {
    localFetchedAt?: string | null;
    hasLocalCatalog?: boolean;
    openSubjectPicker?: boolean;
    force?: boolean;
  } = {}) => {
    if (syncingCatalogRef.current) return;
    syncingCatalogRef.current = true;
    setAppState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      importProgress: 15,
      importProgressText: options.force ? 'Refreshing course catalog...' : 'Checking for schedule updates...',
    }));
    try {
      const localFetchedAt = options.localFetchedAt ?? null;
      const hasLocalCatalog = options.hasLocalCatalog ?? false;
      const meta = await fetchScheduleMeta(options.force);

      if (meta) {
        setCatalogMeta({
          year: meta.year,
          term: meta.term,
          termCode: meta.termCode || '',
        });
      }

      const remoteFetchedAt = meta?.fetchedAt;
      // Only download the full snapshot when we have no local catalog, or the
      // tiny /api/schedule/meta timestamp is newer than what we already have.
      const needFull = Boolean(options.force)
        || !hasLocalCatalog
        || (Boolean(remoteFetchedAt) && catalogIsStale(localFetchedAt, remoteFetchedAt));

      if (!needFull) {
        setAppState(prev => ({
          ...prev,
          catalogFetchedAt: remoteFetchedAt || prev.catalogFetchedAt,
          catalogTermCode: meta?.termCode || prev.catalogTermCode,
          catalogTermLabel: meta ? `${meta.term} ${meta.year}` : prev.catalogTermLabel,
          isLoading: false,
          error: null,
          importProgress: 0,
          importProgressText: '',
        }));
        if (options.openSubjectPicker) setImportModalOpen(true);
        return;
      }

      setAppState(prev => ({
        ...prev,
        importProgress: 45,
        importProgressText: 'Loading course catalog...',
      }));
      const snapshot = await fetchScheduleSnapshot(options.force);
      if (!options.force && hasLocalCatalog && !catalogIsStale(localFetchedAt, snapshot.fetchedAt)) {
        setCatalogMeta({
          year: snapshot.year,
          term: snapshot.term,
          termCode: snapshot.termCode || '',
        });
        setAppState(prev => ({
          ...prev,
          catalogFetchedAt: snapshot.fetchedAt,
          catalogTermCode: snapshot.termCode || prev.catalogTermCode,
          catalogTermLabel: `${snapshot.term} ${snapshot.year}`,
          isLoading: false,
          error: null,
          importProgress: 0,
          importProgressText: '',
        }));
      } else {
        applySnapshot(snapshot, options);
      }
      if (options.openSubjectPicker) setImportModalOpen(true);
    } catch (error) {
      console.error('Failed to sync catalog:', error);
      setAppState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load schedule',
        importProgress: 0,
        importProgressText: '',
      }));
      if (options.openSubjectPicker) setImportModalOpen(true);
    } finally {
      syncingCatalogRef.current = false;
    }
  }, [applySnapshot]);

  // Load data on mount
  React.useEffect(() => {
    console.log('🔍 Checking for saved data on mount...');
    const savedData = loadFromLocalStorage();
    console.log('🔍 Saved data found:', !!savedData);
    if (savedData && hasRestorableSession(savedData)) {
      console.log('🔍 Setting restore prompt to true');
      setShowRestorePrompt(true);
      return;
    }
    setImportModalOpen(true);
    if (savedData?.allCourses.length) {
      setAppState(prev => ({
        ...prev,
        allCourses: savedData.allCourses,
        onlineCourses: savedData.onlineCourses,
        subjects: savedData.subjects,
        courses: savedData.courses,
        instructors: savedData.instructors,
        campuses: savedData.campuses,
        subjectData: savedData.subjectData as Map<string, SubjectData>,
        catalogFetchedAt: savedData.catalogFetchedAt || null,
        catalogTermCode: savedData.catalogTermCode,
        catalogTermLabel: savedData.catalogTermLabel,
        mySchedule: [],
        myOnlineClasses: [],
        customBlocks: [],
        filters: emptyFilters(),
      }));
      setIsLightMode(savedData.isLightMode);
      void syncCatalog({
        localFetchedAt: savedData.catalogFetchedAt || null,
        hasLocalCatalog: true,
        mySchedule: [],
        myOnlineClasses: [],
        customBlocks: [],
        localTermCode: savedData.catalogTermCode,
        localTermLabel: savedData.catalogTermLabel,
        localCourses: savedData.allCourses,
      });
    }
  }, [loadFromLocalStorage, syncCatalog]);

  const handleRestoreData = useCallback(() => {
    const savedData = loadFromLocalStorage();
    if (savedData) {
      setAppState(prev => ({
        ...prev,
        allCourses: savedData.allCourses,
        onlineCourses: savedData.onlineCourses,
        mySchedule: savedData.mySchedule,
        myOnlineClasses: savedData.myOnlineClasses,
        customBlocks: savedData.customBlocks || [],
        subjects: savedData.subjects,
        courses: savedData.courses,
        instructors: savedData.instructors,
        campuses: savedData.campuses,
        subjectData: savedData.subjectData as Map<string, SubjectData>,
        filters: savedData.filters,
        catalogFetchedAt: savedData.catalogFetchedAt || null,
        catalogTermCode: savedData.catalogTermCode,
        catalogTermLabel: savedData.catalogTermLabel,
      }));
      setIsLightMode(savedData.isLightMode);
      setShowRestorePrompt(false);
      void syncCatalog({
        localFetchedAt: savedData.catalogFetchedAt || null,
        hasLocalCatalog: (savedData.allCourses?.length || 0) > 0,
        mySchedule: savedData.mySchedule,
        myOnlineClasses: savedData.myOnlineClasses,
        customBlocks: savedData.customBlocks || [],
        localTermCode: savedData.catalogTermCode,
        localTermLabel: savedData.catalogTermLabel,
        localCourses: savedData.allCourses,
        openSubjectPicker: savedData.filters.neededCourses.size === 0 && savedData.filters.subjectAllow.size === 0,
      });
      console.log('🔄 Data restored from local storage');
      console.log('🔄 Custom blocks restored:', savedData.customBlocks?.length || 0);
    }
  }, [loadFromLocalStorage, syncCatalog]);

  const handleDiscardData = useCallback(() => {
    const savedData = loadFromLocalStorage();
    syncingCatalogRef.current = false;
    setShowRestorePrompt(false);

    if (savedData?.allCourses.length) {
      persistCatalogOnly(savedData);
      setAppState(prev => ({
        ...prev,
        allCourses: savedData.allCourses,
        onlineCourses: savedData.onlineCourses,
        subjects: savedData.subjects,
        courses: savedData.courses,
        instructors: savedData.instructors,
        campuses: savedData.campuses,
        subjectData: savedData.subjectData as Map<string, SubjectData>,
        catalogFetchedAt: savedData.catalogFetchedAt || null,
        catalogTermCode: savedData.catalogTermCode,
        catalogTermLabel: savedData.catalogTermLabel,
        mySchedule: [],
        myOnlineClasses: [],
        customBlocks: [],
        filters: emptyFilters(),
      }));
      setIsLightMode(savedData.isLightMode);
      setImportModalOpen(true);
      void syncCatalog({
        localFetchedAt: savedData.catalogFetchedAt || null,
        hasLocalCatalog: true,
        mySchedule: [],
        myOnlineClasses: [],
        customBlocks: [],
        localTermCode: savedData.catalogTermCode,
        localTermLabel: savedData.catalogTermLabel,
        localCourses: savedData.allCourses,
      });
      console.log('❌ Saved schedule discarded; kept local catalog');
      return;
    }

    clearLocalStorage();
    setImportModalOpen(true);
    console.log('❌ Saved data discarded');
  }, [loadFromLocalStorage, persistCatalogOnly, syncCatalog, clearLocalStorage]);

  const currentCatalogSyncOptions = useCallback((): CatalogSyncOptions & { localFetchedAt: string | null; hasLocalCatalog: boolean } => ({
    localFetchedAt: appState.catalogFetchedAt,
    hasLocalCatalog: appState.allCourses.length > 0,
    mySchedule: appState.mySchedule,
    myOnlineClasses: appState.myOnlineClasses,
    customBlocks: appState.customBlocks,
    localTermCode: appState.catalogTermCode,
    localTermLabel: appState.catalogTermLabel,
    localCourses: appState.allCourses,
  }), [appState]);

  const handleLoadCatalog = useCallback(async () => {
    await syncCatalog(currentCatalogSyncOptions());
  }, [syncCatalog, currentCatalogSyncOptions]);

  const refreshCatalogFromConsole = useCallback(async () => {
    syncingCatalogRef.current = false;
    console.info('Refreshing course catalog…');
    await syncCatalog({ ...currentCatalogSyncOptions(), force: true });
    console.info('Course catalog refresh finished.');
  }, [syncCatalog, currentCatalogSyncOptions]);

  useEffect(() => {
    window.refreshCatalog = refreshCatalogFromConsole;
    return () => {
      if (window.refreshCatalog === refreshCatalogFromConsole) {
        delete window.refreshCatalog;
      }
    };
  }, [refreshCatalogFromConsole]);

  const handleSaveLoadSchedule = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setSaveLoadMenuAnchor(event.currentTarget);
  }, []);

  const handleSaveScheduleClick = useCallback(() => {
    setSaveLoadMenuAnchor(null);
    setScheduleName('');
    setEditingScheduleId(null);
    setSaveScheduleModalOpen(true);
  }, []);

  const [saveLoadModalTab, setSaveLoadModalTab] = useState<number>(0);

  const handleShareScheduleClick = useCallback(() => {
    setSaveLoadMenuAnchor(null);
    setSaveLoadModalTab(0); // Save tab
    setSaveLoadModalOpen(true);
  }, []);

  const handleLoadScheduleClick = useCallback(() => {
    setSaveLoadMenuAnchor(null);
    setSaveLoadModalTab(1); // Load tab
    setSaveLoadModalOpen(true);
  }, []);

  // Load saved schedules from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ssb_saved_schedules');
      if (saved) {
        const schedules = JSON.parse(saved);
        // Validate and restore schedules
        if (Array.isArray(schedules) && schedules.length > 0) {
          setSavedSchedules(schedules);
        }
      }
    } catch (error) {
      console.error('Failed to load saved schedules:', error);
    }
  }, []);

  // Save schedules to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('ssb_saved_schedules', JSON.stringify(savedSchedules));
    } catch (error) {
      console.error('Failed to save schedules:', error);
    }
  }, [savedSchedules]);

  const handleSaveSchedule = useCallback(() => {
    if (!scheduleName.trim()) {
      alert('Please enter a name for the schedule');
      return;
    }

    const newSchedule: SavedSchedule = {
      id: editingScheduleId || `schedule_${Date.now()}`,
      name: scheduleName.trim(),
      mySchedule: appState.mySchedule,
      myOnlineClasses: appState.myOnlineClasses,
      customBlocks: appState.customBlocks,
      termCode: appState.catalogTermCode || undefined,
      termLabel: appState.catalogTermLabel || undefined,
      createdAt: editingScheduleId 
        ? savedSchedules.find(s => s.id === editingScheduleId)?.createdAt || Date.now()
        : Date.now(),
      updatedAt: Date.now(),
    };

    if (editingScheduleId) {
      setSavedSchedules(prev => prev.map(s => s.id === editingScheduleId ? newSchedule : s));
    } else {
      setSavedSchedules(prev => [...prev, newSchedule]);
    }

    setSaveScheduleModalOpen(false);
    setScheduleName('');
    setEditingScheduleId(null);
  }, [scheduleName, appState.mySchedule, appState.myOnlineClasses, appState.customBlocks, appState.catalogTermCode, appState.catalogTermLabel, editingScheduleId, savedSchedules]);

  const handleLoadSavedSchedule = useCallback((scheduleId: string) => {
    const schedule = savedSchedules.find(s => s.id === scheduleId);
    if (schedule) {
      const fromOtherTerm = Boolean(schedule.termCode && appState.catalogTermCode && schedule.termCode !== appState.catalogTermCode);
      const hasCatalog = appState.allCourses.length > 0;
      const mySchedule = fromOtherTerm
        ? markUnavailable(schedule.mySchedule)
        : hasCatalog ? rematchSavedCourses(schedule.mySchedule, appState.allCourses) : schedule.mySchedule;
      const myOnlineClasses = fromOtherTerm
        ? markUnavailable(schedule.myOnlineClasses)
        : hasCatalog ? rematchSavedCourses(schedule.myOnlineClasses, appState.onlineCourses) : schedule.myOnlineClasses;
      setAppState(prev => ({
        ...prev,
        mySchedule,
        myOnlineClasses,
        customBlocks: schedule.customBlocks,
      }));
      setSelectedScheduleId(scheduleId);
      if (fromOtherTerm) {
        setCatalogNotice(`"${schedule.name}" is from ${schedule.termLabel || 'an earlier semester'}. Its classes are shown for reference and aren't in the ${catalogMeta.term} ${catalogMeta.year} schedule.`);
      } else {
        const lost = countNewlyUnavailable(schedule.mySchedule, mySchedule) + countNewlyUnavailable(schedule.myOnlineClasses, myOnlineClasses);
        if (lost > 0) {
          setCatalogNotice(`${lost} ${lost === 1 ? 'class' : 'classes'} in "${schedule.name}" ${lost === 1 ? 'is' : 'are'} no longer listed by COS.`);
        }
      }
    }
  }, [savedSchedules, appState.catalogTermCode, appState.allCourses, appState.onlineCourses, catalogMeta.term, catalogMeta.year]);

  const handleDeleteSchedule = useCallback((scheduleId: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Are you sure you want to delete this schedule?')) {
      setSavedSchedules(prev => prev.filter(s => s.id !== scheduleId));
      if (selectedScheduleId === scheduleId) {
        setSelectedScheduleId('');
      }
      if (compareSchedule1Id === scheduleId || compareSchedule2Id === scheduleId) {
        setCompareSchedule1Id('');
        setCompareSchedule2Id('');
        setCompareMode(false);
      }
    }
  }, [selectedScheduleId, compareSchedule1Id, compareSchedule2Id]);

  const handleEditSchedule = useCallback(() => {
    if (!selectedScheduleId) return;
    const schedule = savedSchedules.find(s => s.id === selectedScheduleId);
    if (schedule) {
      setScheduleName(schedule.name);
      setEditingScheduleId(schedule.id);
      setEditScheduleModalOpen(true);
    }
  }, [selectedScheduleId, savedSchedules]);

  const handleRenameSchedule = useCallback(() => {
    if (!scheduleName.trim()) {
      alert('Please enter a name for the schedule');
      return;
    }
    if (!editingScheduleId) return;

    const schedule = savedSchedules.find(s => s.id === editingScheduleId);
    if (schedule) {
      const updatedSchedule: SavedSchedule = {
        ...schedule,
        name: scheduleName.trim(),
        updatedAt: Date.now(),
      };
      setSavedSchedules(prev => prev.map(s => s.id === editingScheduleId ? updatedSchedule : s));
    }

    setEditScheduleModalOpen(false);
    setScheduleName('');
    setEditingScheduleId(null);
  }, [scheduleName, editingScheduleId, savedSchedules]);

  const handleStartCompare = useCallback(() => {
    if (!compareSchedule1Id || !compareSchedule2Id) {
      alert('Please select two schedules to compare');
      return;
    }
    if (compareSchedule1Id === compareSchedule2Id) {
      alert('Please select two different schedules to compare');
      return;
    }
    setCompareMode(true);
    setCompareMenuAnchor(null);
  }, [compareSchedule1Id, compareSchedule2Id]);

  const handleExitCompare = useCallback(() => {
    setCompareMode(false);
    setCompareSchedule1Id('');
    setCompareSchedule2Id('');
  }, []);

  const handleCreateCustomBlock = useCallback(() => {
    setEditingCustomBlock(null);
    setCustomBlockModalOpen(true);
  }, []);

  const handleEditCustomBlock = useCallback((blockId: string) => {
    const block = appState.customBlocks.find(b => b.id === blockId);
    if (block) {
      setEditingCustomBlock(block);
      setCustomBlockModalOpen(true);
    }
  }, [appState.customBlocks]);

  const handleSaveCustomBlock = useCallback((block: CustomTimeBlock) => {
    setAppState(prev => ({
      ...prev,
      customBlocks: editingCustomBlock 
        ? prev.customBlocks.map(b => b.id === block.id ? block : b)
        : [...prev.customBlocks, block]
    }));
  }, [editingCustomBlock]);

  const handleDeleteCustomBlock = useCallback((blockId: string) => {
    setAppState(prev => ({
      ...prev,
      customBlocks: prev.customBlocks.filter(b => b.id !== blockId)
    }));
  }, []);

  const handleLoadEncodedSchedule = useCallback(async (encodedString: string) => {
    setAppState(prev => ({ 
      ...prev, 
      isLoading: true, 
      error: null,
      importProgress: 0,
      importProgressText: 'Decoding schedule data...'
    }));

    try {
      const { decodeScheduleData } = await import('./utils/parser');
      const decodedData = decodeScheduleData(encodedString);
      
      if (!decodedData) {
        throw new Error('Invalid encoded schedule data');
      }

      setAppState(prev => ({ 
        ...prev, 
        importProgress: 50,
        importProgressText: 'Looking up courses from available schedule...'
      }));

      // Look up courses from available courses with better matching
      const regularCourses: any[] = [];
      const missingRegular: string[] = [];

      for (const courseData of decodedData.c) {
        if (Array.isArray(courseData) && courseData.length >= 6) {
          // New format: [CRN, Subject, Course, Days, StartMin, EndMin]
          const [crn, subject, course, days, startMin, endMin] = courseData;
          
          // Find all courses that match this identifier
          const matchingCourses = appState.allCourses.filter(availableCourse => 
            availableCourse.CRN === crn &&
            availableCourse.Subject === subject &&
            availableCourse.Course === course &&
            availableCourse.Days === days &&
            availableCourse.StartMin === startMin &&
            availableCourse.EndMin === endMin
          );
          
          if (matchingCourses.length > 0) {
            regularCourses.push(...matchingCourses);
          } else {
            missingRegular.push(`${subject} ${course} (${crn})`);
          }
        } else if (typeof courseData === 'string') {
          // Old format: just CRN string
          const matchingCourses = appState.allCourses.filter(course => course.CRN === courseData);
          if (matchingCourses.length > 0) {
            regularCourses.push(...matchingCourses);
          } else {
            missingRegular.push(courseData);
          }
        }
      }

      const onlineCourses: any[] = [];
      const missingOnline: string[] = [];

      for (const courseData of decodedData.o) {
        if (Array.isArray(courseData) && courseData.length >= 3) {
          // New format: [CRN, Subject, Course]
          const [crn, subject, course] = courseData;
          
          const matchingCourses = appState.onlineCourses.filter(availableCourse => 
            availableCourse.CRN === crn &&
            availableCourse.Subject === subject &&
            availableCourse.Course === course
          );
          
          if (matchingCourses.length > 0) {
            onlineCourses.push(...matchingCourses);
          } else {
            missingOnline.push(`${subject} ${course} (${crn})`);
          }
        } else if (typeof courseData === 'string') {
          // Old format: just CRN string
          const matchingCourses = appState.onlineCourses.filter(course => course.CRN === courseData);
          if (matchingCourses.length > 0) {
            onlineCourses.push(...matchingCourses);
          } else {
            missingOnline.push(courseData);
          }
        }
      }

      if (missingRegular.length > 0 || missingOnline.length > 0) {
        const missing = [...missingRegular, ...missingOnline];
        throw new Error(`Some courses not found in available schedule: ${missing.join(', ')}. Please make sure you have loaded the correct available courses first.`);
      }

      // Convert custom blocks from encoded format
      const customBlocks = decodedData.b ? decodedData.b.map((blockArray: any[]) => decodeCustomBlockFromShare(blockArray)) : [];

      // Restore the schedule data
      setAppState(prev => ({
        ...prev,
        mySchedule: regularCourses,
        myOnlineClasses: onlineCourses,
        customBlocks: customBlocks,
        isLoading: false,
        importProgress: 100,
        importProgressText: 'Schedule restored successfully!'
      }));

      // Clear progress after a short delay
      setTimeout(() => {
        setAppState(prev => ({ 
          ...prev, 
          importProgress: 0,
          importProgressText: ''
        }));
      }, 2000);

    } catch (error) {
      console.error('Failed to load encoded schedule:', error);
      setAppState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to load encoded schedule'
      }));
    }
  }, [appState.allCourses, appState.onlineCourses]);


  const handleFilterChange = useCallback((newFilters: Partial<FilterState>) => {
    setAppState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...newFilters },
    }));
  }, []);

  const handleAddCourse = useCallback((crn: string) => {
    setAppState(prev => {
      const course = prev.allCourses.find(c => c.CRN === crn);
      if (!course || prev.mySchedule.find(c => c.CRN === crn && !c.unavailable)) {
        return prev;
      }

      const coursesWithSameCRN = prev.allCourses.filter(c => c.CRN === crn);
      const occupied = occupiedMeetings(prev.mySchedule, prev.customBlocks);
      const conflictName = firstConflictName(coursesWithSameCRN, occupied);
      if (conflictName) {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm(`Time conflict with ${conflictName}. Add anyway?`)) {
          return prev;
        }
      }
      
      // Add all courses with the same CRN
      const newSchedule = [...prev.mySchedule];
      coursesWithSameCRN.forEach(crs => {
        const alreadyExists = newSchedule.find(c => 
          !c.unavailable &&
          c.CRN === crs.CRN && 
          c.Days === crs.Days && 
          c.DispTime === crs.DispTime
        );
        
        if (!alreadyExists) {
          newSchedule.push(crs);
        }
      });

      // Hybrid sections: the online part of the same CRN goes to My Online Classes.
      const onlineParts = prev.onlineCourses.filter(c => c.CRN === crn);
      const hasOnlinePart = prev.myOnlineClasses.some(c => c.CRN === crn && !c.unavailable);
      
      return {
        ...prev,
        mySchedule: newSchedule,
        myOnlineClasses: hasOnlinePart ? prev.myOnlineClasses : [...prev.myOnlineClasses, ...onlineParts],
      };
    });
  }, []);

  const handleRemoveCourse = useCallback((crn: string) => {
    setAppState(prev => ({
      ...prev,
      mySchedule: prev.mySchedule.filter(c => c.CRN !== crn),
      myOnlineClasses: prev.myOnlineClasses.filter(c => c.CRN !== crn),
    }));
  }, []);

  const handleAddOnlineCourse = useCallback((crn: string) => {
    setAppState(prev => {
      const onlineParts = prev.onlineCourses.filter(c => c.CRN === crn);
      if (onlineParts.length === 0 || prev.myOnlineClasses.find(c => c.CRN === crn && !c.unavailable)) {
        return prev;
      }

      // Hybrid sections: in-person meetings of the same CRN go on the calendar.
      const meetings = prev.allCourses.filter(c => c.CRN === crn);
      const alreadyOnCalendar = prev.mySchedule.some(c => c.CRN === crn && !c.unavailable);
      if (meetings.length > 0 && !alreadyOnCalendar) {
        const conflictName = firstConflictName(meetings, occupiedMeetings(prev.mySchedule, prev.customBlocks));
        // eslint-disable-next-line no-restricted-globals
        if (conflictName && !confirm(`Time conflict with ${conflictName}. Add anyway?`)) {
          return prev;
        }
      }

      return {
        ...prev,
        mySchedule: alreadyOnCalendar ? prev.mySchedule : [...prev.mySchedule, ...meetings],
        myOnlineClasses: [...prev.myOnlineClasses, ...onlineParts],
      };
    });
  }, []);

  const handleRemoveOnlineCourse = useCallback((crn: string) => {
    setAppState(prev => ({
      ...prev,
      myOnlineClasses: prev.myOnlineClasses.filter(course => course.CRN !== crn),
      mySchedule: prev.mySchedule.filter(course => course.CRN !== crn),
    }));
  }, []);

  const handlePrintSchedule = useCallback(async () => {
    if (appState.mySchedule.length === 0 && appState.myOnlineClasses.length === 0 && appState.customBlocks.length === 0) {
      alert('No courses or custom blocks in your schedule to print');
      return;
    }

    // Use the component-level totalUnits calculation

    // Generate encoded string for sharing
    const generateEncodedString = () => {
      const scheduleData = {
        v: '1.0',
        t: 'ssb',
        ts: Date.now(),
        c: appState.mySchedule.map(course => [
          course.CRN,
          course.Subject,
          course.Course,
          course.Days,
          course.StartMin,
          course.EndMin
        ]),
        o: appState.myOnlineClasses.map(course => [
          course.CRN,
          course.Subject,
          course.Course
        ]),
        b: appState.customBlocks.map(block => encodeCustomBlockForShare(block))
      };
      const jsonString = JSON.stringify(scheduleData);
      return btoa(unescape(encodeURIComponent(jsonString)));
    };

    const encodedString = generateEncodedString();

    // Generate QR code
    let qrCodeDataUrl = '';
    try {
      const QRCode = (await import('qrcode')).default;
      qrCodeDataUrl = await QRCode.toDataURL(encodedString, { 
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }

    // Calculate dynamic time range based on my schedule
    const calculateTimeRange = () => {
      if (appState.mySchedule.length === 0) {
        return { startHour: 8, endHour: 22 }; // Default fallback
      }
      
      const allStartMins = appState.mySchedule.map(course => course.StartMin);
      const allEndMins = appState.mySchedule.map(course => course.EndMin);
      
      const earliestStart = Math.min(...allStartMins);
      const latestEnd = Math.max(...allEndMins);
      
      // Convert to hours and add buffer
      const startHour = Math.floor(earliestStart / 60) - 1; // 1 hour before first class
      const endHour = Math.ceil(latestEnd / 60) + 1; // 1 hour after last class
      
      // Ensure reasonable bounds
      return {
        startHour: Math.max(6, startHour), // No earlier than 6 AM
        endHour: Math.min(23, endHour)     // No later than 11 PM
      };
    };

    const timeRange = calculateTimeRange();
    const totalHours = timeRange.endHour - timeRange.startHour;

    // Group courses by CRN to avoid duplicates
    const uniqueCourses = [];
    const seenCRNs = new Set();

    appState.mySchedule.forEach(course => {
      if (!seenCRNs.has(course.CRN)) {
        seenCRNs.add(course.CRN);
        uniqueCourses.push(course);
      }
    });

    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>My Schedule - ${totalUnits} Units</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #000; background: #fff; }
          .header { text-align: center; margin-bottom: 30px; }
          .total-units { font-size: 24px; font-weight: bold; color: #2563eb; margin: 10px 0; }
          .schedule-grid { display: grid; grid-template-columns: 60px repeat(5, 1fr); gap: 1px; border: 1px solid #ccc; page-break-inside: avoid; }
          .time-header, .day-header { background: #f0f4f8; padding: 8px; text-align: center; font-weight: bold; border: 1px solid #ccc; }
          .time-col { background: #f8fafc; }
          .day-col { background: #fff; position: relative; min-height: 400px; }
          .slot { position: absolute; left: 2px; right: 2px; padding: 4px; border-radius: 4px; font-size: 10px; border: 1px solid #000; }
          .slot strong { display: block; font-size: 11px; }
          .slot small { display: block; font-size: 9px; }
          .course-table { margin-top: 30px; page-break-before: always; }
          .course-table table { width: 100%; border-collapse: collapse; }
          .course-table th, .course-table td { border: 1px solid #ccc; padding: 8px; text-align: left; }
          .course-table th { background: #f0f4f8; font-weight: bold; }
          .tick { font-size: 10px; padding: 2px; border-bottom: 1px dashed #ccc; height: 40px; }
          
          @media print {
            .course-table { 
              page-break-before: always;
              page-orientation: landscape;
            }
            .course-table table {
              font-size: 12px;
            }
            .course-table th, .course-table td {
              padding: 6px 4px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>My Course Schedule</h1>
          <div class="total-units">Total Units: ${totalUnits}</div>
          <p>Generated on ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div class="schedule-grid">
          <div class="time-header">Time</div>
          <div class="day-header">Mon</div>
          <div class="day-header">Tue</div>
          <div class="day-header">Wed</div>
          <div class="day-header">Thu</div>
          <div class="day-header">Fri</div>
          
          <div class="time-col">
            ${Array.from({length: totalHours}, (_, i) => {
              const hour = timeRange.startHour + i;
              const ampm = hour >= 12 ? 'PM' : 'AM';
              const h12 = ((hour + 11) % 12) + 1;
              return `<div class="tick">${h12}:00 ${ampm}</div>`;
            }).join('')}
          </div>
          
          ${['M','T','W','R','F'].map(day => {
            const dayCourses = appState.mySchedule.filter(c => c.Days.includes(day));
            const dayCustomBlocks = appState.customBlocks.filter(block => block.days.includes(day));
            return `
              <div class="day-col">
                ${dayCourses.map(course => {
                  const startHour = timeRange.startHour;
                  const top = ((course.StartMin - (startHour * 60)) / 60) * 40;
                  const height = ((course.EndMin - course.StartMin) / 60) * 40;
                  return `
                    <div class="slot" style="top: ${top}px; height: ${height}px; background: ${course.__bg}; border-color: ${course.__color};">
                      <strong>${course.Subject} ${course.Course}</strong>
                      <small>${course.DispTime}</small>
                      <small>${course.Location}</small>
                    </div>
                  `;
                }).join('')}
                ${dayCustomBlocks.map(block => {
                  const startHour = timeRange.startHour;
                  const startMinutes = parseInt(block.times[day].start);
                  const endMinutes = parseInt(block.times[day].end);
                  const top = ((startMinutes - (startHour * 60)) / 60) * 40;
                  const height = ((endMinutes - startMinutes) / 60) * 40;
                  const startTime = Math.floor(startMinutes / 60);
                  const startMin = startMinutes % 60;
                  const endTime = Math.floor(endMinutes / 60);
                  const endMin = endMinutes % 60;
                  const formatTime = (hour: number, min: number) => {
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const h12 = ((hour + 11) % 12) + 1;
                    return `${h12}:${min.toString().padStart(2, '0')} ${ampm}`;
                  };
                  return `
                    <div class="slot" style="top: ${top}px; height: ${height}px; background: ${block.color}; border-color: ${block.color};">
                      <strong>${block.title}</strong>
                      <small>${formatTime(startTime, startMin)} - ${formatTime(endTime, endMin)}</small>
                      ${block.instructor ? `<small>${block.instructor}</small>` : ''}
                      ${block.location ? `<small>${block.location}</small>` : ''}
                      ${block.customField ? `<small>${block.customField}</small>` : ''}
                    </div>
                  `;
                }).join('')}
              </div>
            `;
          }).join('')}
        </div>
        
        <div class="course-table">
          <h2>Schedule Details</h2>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Subject</th>
                <th>Course</th>
                <th>Title</th>
                <th>CRN</th>
                <th>Units</th>
                <th>Instructor</th>
                <th>Days</th>
                <th>Time</th>
                <th>Location</th>
                <th>Campus</th>
              </tr>
            </thead>
            <tbody>
              ${uniqueCourses.map(course => `
                <tr>
                  <td>Course</td>
                  <td>${course.Subject}</td>
                  <td>${course.Course}</td>
                  <td>${course.Title}</td>
                  <td>${course.CRN}</td>
                  <td>${course.Units}</td>
                  <td>${course.Instructor || 'TBA'}</td>
                  <td>${course.Days}</td>
                  <td>${course.DispTime}</td>
                  <td>${course.Location}</td>
                  <td>${course.Campus}</td>
                </tr>
              `).join('')}
              ${appState.myOnlineClasses.map(course => `
                <tr>
                  <td>Online Course</td>
                  <td>${course.Subject}</td>
                  <td>${course.Course}</td>
                  <td>${course.Title}</td>
                  <td>${course.CRN}</td>
                  <td>${course.Units}</td>
                  <td>${course.Instructor || 'TBA'}</td>
                  <td>Online</td>
                  <td>Online</td>
                  <td>Online</td>
                  <td>${course.Campus}</td>
                </tr>
              `).join('')}
              ${appState.customBlocks.map(block => {
                const formatTime = (hour: number, min: number) => {
                  const ampm = hour >= 12 ? 'PM' : 'AM';
                  const h12 = ((hour + 11) % 12) + 1;
                  return `${h12}:${min.toString().padStart(2, '0')} ${ampm}`;
                };
                const formatTimes = () => {
                  const timeStrings = block.days.map(day => {
                    const startMinutes = parseInt(block.times[day].start);
                    const endMinutes = parseInt(block.times[day].end);
                    const startTime = Math.floor(startMinutes / 60);
                    const startMin = startMinutes % 60;
                    const endTime = Math.floor(endMinutes / 60);
                    const endMin = endMinutes % 60;
                    return `${day}: ${formatTime(startTime, startMin)} - ${formatTime(endTime, endMin)}`;
                  });
                  return timeStrings.join(', ');
                };
                return `
                  <tr>
                    <td>Custom Block</td>
                    <td>-</td>
                    <td>-</td>
                    <td>${block.title}${block.customField ? ` — ${block.customField}` : ''}</td>
                    <td>${block.crn || '-'}</td>
                    <td>-</td>
                    <td>${block.instructor || '-'}</td>
                    <td>${block.days.join('')}</td>
                    <td>${formatTimes()}</td>
                    <td>${block.location || '-'}</td>
                    <td>${block.campus || '-'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="sharing-section" style="margin-top: 40px; page-break-before: always; text-align: center;">
          <h2>Share This Schedule</h2>
          <p style="margin-bottom: 20px; color: #666;">
            Scan the QR code below or copy the encoded string to share this schedule with others.
          </p>
          
          ${qrCodeDataUrl ? `
            <div style="margin: 20px 0;">
              <img src="${qrCodeDataUrl}" alt="Schedule QR Code" style="border: 1px solid #ccc; padding: 10px; background: white;" />
            </div>
          ` : ''}
          
          <div style="margin: 20px 0; padding: 15px; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 5px;">
            <h3 style="margin: 0 0 10px 0; font-size: 16px; color: #495057;">Encoded Schedule String:</h3>
            <div style="background: white; padding: 10px; border: 1px solid #ccc; border-radius: 3px; font-family: monospace; font-size: 12px; word-break: break-all; text-align: left; max-height: 200px; overflow-y: auto;">
              ${encodedString}
            </div>
            <p style="margin: 10px 0 0 0; font-size: 12px; color: #6c757d;">
              Copy this string and use the "Load" button in the schedule builder to restore this schedule.
            </p>
          </div>
          
          <div style="margin-top: 20px; padding: 10px; background: #e7f3ff; border: 1px solid #b3d9ff; border-radius: 5px; font-size: 14px; color: #0066cc;">
            <strong>Instructions:</strong> To load this schedule, open the schedule builder, select the same subjects, then use the "Load" button and paste the encoded string above.
          </div>
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load, then print
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    }
  }, [appState.mySchedule, appState.myOnlineClasses, appState.customBlocks, totalUnits]);


  const currentTheme = isLightMode ? lightTheme : darkTheme;

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Box sx={{ 
        minHeight: '100vh', 
        background: (theme) => theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #0b0f14 0%, #0d1219 100%)'
          : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        color: 'text.primary'
      }}>
        {/* Header */}
        <Box sx={{
          padding: '16px 20px',
          borderBottom: (theme) => theme.palette.mode === 'dark' 
            ? '1px solid #1e293b' 
            : '1px solid #e5e7eb',
          position: 'sticky',
          top: 0,
          zIndex: 9999,
          background: (theme) => theme.palette.mode === 'dark' 
            ? 'rgba(10,14,20,0.9)' 
            : 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(6px)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontSize: '18px', margin: '0 0 4px 0' }}>
            📚 Student Schedule Builder — {scheduleLabel}
          </Typography>
          {lastUpdatedLabel && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'text.primary',
                margin: '0 0 4px 0',
              }}
            >
              Updated {lastUpdatedLabel}
            </Typography>
          )}
          <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
            Build your {scheduleLabel} course schedule. Start by choosing the classes you need.
          </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<QrCode2 />}
            onClick={() => setShareAppOpen(true)}
            sx={{ flexShrink: 0, textTransform: 'none', borderRadius: '8px' }}
          >
            Share app
          </Button>
          </Box>
        </Box>
        
        {/* Main Grid Layout */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: compareMode 
            ? 'minmax(0, 1fr)'
            : { xs: 'minmax(0, 1fr)', md: '320px minmax(0, 1fr)' },
          gap: '16px',
          padding: '16px',
          minHeight: 'calc(100vh - 80px)',
          minWidth: 0,
        }}>
          {/* Left Panel - Filter Panel (hidden in compare mode) */}
          {!compareMode && (
            <Box sx={{
              background: 'background.paper',
              border: (theme) => theme.palette.mode === 'dark' 
                ? '1px solid #1f2937' 
                : '1px solid #e5e7eb',
              borderRadius: '14px',
              padding: '14px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              height: 'fit-content'
            }}>
            {/* Import Button */}
            <Box sx={{ marginBottom: '16px' }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setImportModalOpen(true)}
                disabled={appState.isLoading}
                sx={{
                  background: 'linear-gradient(90deg, #2563eb, #059669)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  padding: '12px 16px',
                  textTransform: 'none',
                  fontWeight: '600',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #1d4ed8, #047857)',
                  },
                  '&:disabled': {
                    background: (theme) => theme.palette.mode === 'dark' ? '#2a3c55' : '#e5e7eb',
                    color: 'text.disabled',
                  }
                }}
              >
                Choose Classes
              </Button>
            </Box>

            <FilterPanel
              filters={appState.filters}
              subjects={appState.subjects}
              courses={appState.courses}
              instructors={appState.instructors}
              campuses={appState.campuses}
              allCourses={appState.allCourses}
              subjectData={appState.subjectData}
              onFilterChange={handleFilterChange}
              isLightMode={isLightMode}
              onToggleLightMode={() => setIsLightMode(!isLightMode)}
            />
          </Box>
          )}
          
          {/* Right Grid / Compare Mode */}
          {compareMode ? (
            /* Compare Mode */
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              {/* Compare Mode Header */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                p: 2,
                background: (theme) => theme.palette.mode === 'dark' ? '#1a2330' : '#ffffff',
                border: (theme) => theme.palette.mode === 'dark' 
                  ? '1px solid #203045' 
                  : '1px solid #d1d5db',
                borderRadius: '16px',
              }}>
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                  Compare Schedules
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Schedule 1</InputLabel>
                    <Select
                      value={compareSchedule1Id}
                      onChange={(e) => setCompareSchedule1Id(e.target.value as string)}
                      label="Schedule 1"
                    >
                      {savedSchedules.map((schedule) => (
                        <MenuItem key={schedule.id} value={schedule.id}>
                          {schedule.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Schedule 2</InputLabel>
                    <Select
                      value={compareSchedule2Id}
                      onChange={(e) => setCompareSchedule2Id(e.target.value as string)}
                      label="Schedule 2"
                    >
                      {savedSchedules.map((schedule) => (
                        <MenuItem key={schedule.id} value={schedule.id}>
                          {schedule.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Exit Compare Mode">
                    <IconButton
                      onClick={handleExitCompare}
                      sx={{
                        background: 'error.main',
                        color: 'white',
                        '&:hover': {
                          background: 'error.dark',
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                gap: '16px',
                width: '100%',
                minWidth: 0,
              }}>
              {/* Compare Schedule 1 */}
              {(() => {
                const schedule1 = savedSchedules.find(s => s.id === compareSchedule1Id);
                if (!schedule1) return null;
                return (
                  <Box sx={{
                    background: (theme) => theme.palette.mode === 'dark' ? '#1a2330' : '#ffffff',
                    border: (theme) => theme.palette.mode === 'dark' 
                      ? '1px solid #203045' 
                      : '1px solid #d1d5db',
                    borderRadius: '16px',
                    padding: '10px',
                    minHeight: '680px',
                    minWidth: 0,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <Typography variant="h6" sx={{
                        fontSize: '15px',
                        color: 'text.secondary'
                      }}>
                        {schedule1.name}
                      </Typography>
                    </Box>
                    <ScheduleGrid
                      courses={schedule1.mySchedule}
                      mySchedule={schedule1.mySchedule}
                      customBlocks={schedule1.customBlocks}
                      filters={appState.filters}
                      onAddCourse={() => {}} // Disabled in compare mode
                      onRemoveCourse={() => {}} // Disabled in compare mode
                      onEditCustomBlock={handleEditCustomBlock}
                      courseOpacity={courseOpacity}
                      isMySchedule={true}
                      sharedTimeRange={sharedTimeRange}
                      weekView={weekView}
                    />
                  </Box>
                );
              })()}
              
              {/* Compare Schedule 2 */}
              {(() => {
                const schedule2 = savedSchedules.find(s => s.id === compareSchedule2Id);
                if (!schedule2) return null;
                return (
                  <Box sx={{
                    background: (theme) => theme.palette.mode === 'dark' ? '#1a2330' : '#ffffff',
                    border: (theme) => theme.palette.mode === 'dark' 
                      ? '1px solid #203045' 
                      : '1px solid #d1d5db',
                    borderRadius: '16px',
                    padding: '10px',
                    minHeight: '680px',
                    minWidth: 0,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <Typography variant="h6" sx={{
                        fontSize: '15px',
                        color: 'text.secondary'
                      }}>
                        {schedule2.name}
                      </Typography>
                    </Box>
                    <ScheduleGrid
                      courses={schedule2.mySchedule}
                      mySchedule={schedule2.mySchedule}
                      customBlocks={schedule2.customBlocks}
                      filters={appState.filters}
                      onAddCourse={() => {}} // Disabled in compare mode
                      onRemoveCourse={() => {}} // Disabled in compare mode
                      onEditCustomBlock={handleEditCustomBlock}
                      courseOpacity={courseOpacity}
                      isMySchedule={true}
                      sharedTimeRange={sharedTimeRange}
                      weekView={weekView}
                    />
                  </Box>
                );
              })()}
              </Box>
            </Box>
          ) : (
            /* Normal Mode */
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: scheduleLayout === 'horizontal' 
                ? { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }
                : { xs: '1fr' },
              gridTemplateRows: scheduleLayout === 'vertical' ? 'auto auto' : 'auto',
              gap: '16px',
              width: '100%',
              minWidth: 0,
            }}>
            {/* Available Courses Board */}
            <Box sx={{
              background: (theme) => theme.palette.mode === 'dark' ? '#1a2330' : '#ffffff',
              border: (theme) => theme.palette.mode === 'dark' 
                ? '1px solid #203045' 
                : '1px solid #d1d5db',
              borderRadius: '16px',
              padding: '10px',
              minHeight: '680px',
              minWidth: 0,
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              position: 'relative'
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '4px 8px 8px 8px'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{
                    fontSize: '15px',
                    color: 'text.secondary'
                  }}>
                    🗓️ Available Courses
                  </Typography>
                  
                  {/* Layout Button */}
                  <IconButton
                    size="small"
                    onClick={(e) => setLayoutMenuAnchor({ ...layoutMenuAnchor, available: e.currentTarget })}
                    sx={{
                      padding: '4px',
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#2a3c55' : '#e5e7eb',
                      },
                    }}
                  >
                    {scheduleLayout === 'horizontal' ? <ViewColumn /> : <ViewAgenda />}
                  </IconButton>
                  <Menu
                    anchorEl={layoutMenuAnchor.available}
                    open={!!layoutMenuAnchor.available}
                    onClose={() => setLayoutMenuAnchor({ ...layoutMenuAnchor, available: null })}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setScheduleLayout('horizontal');
                        setLayoutMenuAnchor({ ...layoutMenuAnchor, available: null });
                      }}
                    >
                      <ListItemIcon>
                        <ViewColumn fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Horizontal Layout</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setScheduleLayout('vertical');
                        setLayoutMenuAnchor({ ...layoutMenuAnchor, available: null });
                      }}
                    >
                      <ListItemIcon>
                        <ViewAgenda fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Vertical Layout</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      selected={weekView === 'full'}
                      onClick={() => {
                        setWeekView('full');
                        setLayoutMenuAnchor({ ...layoutMenuAnchor, available: null });
                      }}
                    >
                      <ListItemIcon>
                        <ViewWeek fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Full week</ListItemText>
                    </MenuItem>
                    <MenuItem
                      selected={weekView === 'twoDay'}
                      onClick={() => {
                        setWeekView('twoDay');
                        setLayoutMenuAnchor({ ...layoutMenuAnchor, available: null });
                      }}
                    >
                      <ListItemIcon>
                        <Swipe fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>2-day scroll</ListItemText>
                    </MenuItem>
                  </Menu>
                </Box>
                
                {/* Opacity Control Menu */}
                <Box sx={{ position: 'relative' }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setShowOpacityMenu(!showOpacityMenu)}
                    sx={{
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      minWidth: 'auto',
                      padding: '4px 8px',
                      fontSize: '12px',
                      borderRadius: '6px',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.8)',
                      },
                    }}
                  >
                    Opacity: {Math.round(courseOpacity * 100)}%
                  </Button>
                  
                  {showOpacityMenu && (
                    <Box sx={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      mt: 1,
                      backgroundColor: 'rgba(0,0,0,0.9)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      minWidth: '200px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      zIndex: 10,
                    }}>
                      <Typography variant="body2" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
                        Course Slot Opacity
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'white', display: 'block', mb: 1 }}>
                          Adjust opacity to see stacked courses clearly
                        </Typography>
                        <input
                          type="range"
                          min="0.3"
                          max="1.0"
                          step="0.1"
                          value={courseOpacity}
                          onChange={(e) => setCourseOpacity(parseFloat(e.target.value))}
                          style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: 'rgba(255,255,255,0.2)',
                            outline: 'none',
                            cursor: 'pointer',
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px' }}>
                            30%
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px' }}>
                            100%
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: '8px' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setCourseOpacity(0.3)}
                          sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.3)',
                            fontSize: '10px',
                            padding: '2px 8px',
                            '&:hover': {
                              borderColor: 'rgba(255,255,255,0.5)',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                            },
                          }}
                        >
                          Low
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setCourseOpacity(0.6)}
                          sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.3)',
                            fontSize: '10px',
                            padding: '2px 8px',
                            '&:hover': {
                              borderColor: 'rgba(255,255,255,0.5)',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                            },
                          }}
                        >
                          Medium
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setCourseOpacity(1.0)}
                          sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.3)',
                            fontSize: '10px',
                            padding: '2px 8px',
                            '&:hover': {
                              borderColor: 'rgba(255,255,255,0.5)',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                            },
                          }}
                        >
                          High
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              <ScheduleGrid
                courses={appState.allCourses}
                mySchedule={appState.mySchedule}
                customBlocks={appState.customBlocks}
                filters={appState.filters}
                onAddCourse={handleAddCourse}
                onRemoveCourse={handleRemoveCourse}
                onEditCustomBlock={handleEditCustomBlock}
                courseOpacity={courseOpacity}
                sharedTimeRange={sharedTimeRange}
                weekView={weekView}
              />
              
              {/* Online Courses Section */}
              {appState.filters.showOnline && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ 
                    mb: 2, 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🌐 Online Courses
                  </Typography>
                  <OnlineCoursesList
                    courses={appState.onlineCourses}
                    myOnlineClasses={appState.myOnlineClasses}
                    filters={appState.filters}
                    onAddOnlineCourse={handleAddOnlineCourse}
                    onRemoveOnlineCourse={handleRemoveOnlineCourse}
                  />
                </Box>
              )}
            </Box>
            
            {/* My Schedule Board */}
            <Box sx={{
              background: (theme) => theme.palette.mode === 'dark' ? '#1a2330' : '#ffffff',
              border: (theme) => theme.palette.mode === 'dark' 
                ? '1px solid #203045' 
                : '1px solid #d1d5db',
              borderRadius: '16px',
              padding: '10px',
              minHeight: '680px',
              minWidth: 0,
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                marginBottom: '8px'
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flex: isNarrowScheduleHeader ? '1 1 auto' : 1,
                  minWidth: 0,
                }}>
                  <Typography variant="h6" sx={{
                    margin: '4px 8px 8px 8px',
                    fontSize: '15px',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}>
                    ✅ My Schedule
                  </Typography>
                  
                  {/* Saved Schedules Dropdown */}
                  {savedSchedules.length > 0 && !isNarrowScheduleHeader && (
                    <FormControl size="small" sx={{ minWidth: 150, mr: 1 }}>
                      <Select
                        value={selectedScheduleId}
                        onChange={(e) => {
                          const id = e.target.value as string;
                          setSelectedScheduleId(id);
                          if (id) {
                            handleLoadSavedSchedule(id);
                          }
                        }}
                        displayEmpty
                        sx={{
                          fontSize: '12px',
                          height: '28px',
                        }}
                      >
                        <MenuItem value="">
                          <em>Current Schedule</em>
                        </MenuItem>
                        {savedSchedules.map((schedule) => (
                          <MenuItem key={schedule.id} value={schedule.id}>
                            {schedule.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  
                  {/* Edit Button */}
                  {selectedScheduleId && (
                    <Tooltip title="Edit Schedule">
                      <IconButton
                        size="small"
                        onClick={handleEditSchedule}
                        sx={{
                          padding: '4px',
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#2a3c55' : '#e5e7eb',
                          },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {/* Compare Button with Menu */}
                  {savedSchedules.length >= 2 && !compareMode && (
                    <>
                      {!isNarrowScheduleHeader && (
                      <Tooltip title="Compare Schedules">
                        <IconButton
                          size="small"
                          onClick={(e) => setCompareMenuAnchor(e.currentTarget)}
                          sx={{
                            padding: '4px',
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#2a3c55' : '#e5e7eb',
                            },
                          }}
                        >
                          <CompareArrows fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      )}
                      <Menu
                        anchorEl={compareMenuAnchor}
                        open={!!compareMenuAnchor}
                        onClose={() => setCompareMenuAnchor(null)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        PaperProps={{
                          sx: {
                            minWidth: 300,
                            p: 2,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Select Schedules to Compare
                          </Typography>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Schedule 1</InputLabel>
                            <Select
                              value={compareSchedule1Id}
                              onChange={(e) => setCompareSchedule1Id(e.target.value as string)}
                              label="Schedule 1"
                            >
                              {savedSchedules.map((schedule) => (
                                <MenuItem key={schedule.id} value={schedule.id}>
                                  {schedule.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                            vs
                          </Typography>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Schedule 2</InputLabel>
                            <Select
                              value={compareSchedule2Id}
                              onChange={(e) => setCompareSchedule2Id(e.target.value as string)}
                              label="Schedule 2"
                            >
                              {savedSchedules.map((schedule) => (
                                <MenuItem key={schedule.id} value={schedule.id}>
                                  {schedule.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="contained"
                            onClick={handleStartCompare}
                            disabled={!compareSchedule1Id || !compareSchedule2Id || compareSchedule1Id === compareSchedule2Id}
                            fullWidth
                            startIcon={<CompareArrows />}
                            sx={{ mt: 1 }}
                          >
                            Compare
                          </Button>
                        </Box>
                      </Menu>
                    </>
                  )}
                  
                  {/* Layout Button */}
                  {!isNarrowScheduleHeader && (
                  <>
                  <IconButton
                    size="small"
                    onClick={(e) => setLayoutMenuAnchor({ ...layoutMenuAnchor, mySchedule: e.currentTarget })}
                    sx={{
                      padding: '4px',
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#2a3c55' : '#e5e7eb',
                      },
                    }}
                  >
                    {scheduleLayout === 'horizontal' ? <ViewColumn /> : <ViewAgenda />}
                  </IconButton>
                  <Menu
                    anchorEl={layoutMenuAnchor.mySchedule}
                    open={!!layoutMenuAnchor.mySchedule}
                    onClose={() => setLayoutMenuAnchor({ ...layoutMenuAnchor, mySchedule: null })}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setScheduleLayout('horizontal');
                        setLayoutMenuAnchor({ ...layoutMenuAnchor, mySchedule: null });
                      }}
                    >
                      <ListItemIcon>
                        <ViewColumn fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Horizontal Layout</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setScheduleLayout('vertical');
                        setLayoutMenuAnchor({ ...layoutMenuAnchor, mySchedule: null });
                      }}
                    >
                      <ListItemIcon>
                        <ViewAgenda fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Vertical Layout</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      selected={weekView === 'full'}
                      onClick={() => {
                        setWeekView('full');
                        setLayoutMenuAnchor({ ...layoutMenuAnchor, mySchedule: null });
                      }}
                    >
                      <ListItemIcon>
                        <ViewWeek fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Full week</ListItemText>
                    </MenuItem>
                    <MenuItem
                      selected={weekView === 'twoDay'}
                      onClick={() => {
                        setWeekView('twoDay');
                        setLayoutMenuAnchor({ ...layoutMenuAnchor, mySchedule: null });
                      }}
                    >
                      <ListItemIcon>
                        <Swipe fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>2-day scroll</ListItemText>
                    </MenuItem>
                  </Menu>
                  </>
                  )}
                </Box>
                <Typography variant="body2" sx={{
                  margin: isNarrowScheduleHeader ? '0 4px' : '0 8px 8px 8px',
                  fontSize: isNarrowScheduleHeader ? '12px' : '14px',
                  fontWeight: 'bold',
                  color: 'white',
                  background: 'primary.main',
                  border: 'none',
                  padding: isNarrowScheduleHeader ? '4px 8px' : '6px 12px',
                  borderRadius: '6px',
                  display: 'inline-block',
                  textTransform: 'none',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}>
                  {totalUnits} Units
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Create Custom Block">
                    <IconButton
                      size="small"
                      onClick={handleCreateCustomBlock}
                      sx={{
                        background: 'warning.main',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px',
                        '&:hover': {
                          background: 'warning.dark',
                        },
                      }}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!isNarrowScheduleHeader && (
                  <>
                  <Tooltip title="Save / Load Schedule">
                    <IconButton
                      size="small"
                      onClick={handleSaveLoadSchedule}
                      sx={{
                        background: 'secondary.main',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px',
                        '&:hover': {
                          background: 'secondary.dark',
                        },
                      }}
                    >
                      <Save fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={saveLoadMenuAnchor}
                    open={!!saveLoadMenuAnchor}
                    onClose={() => setSaveLoadMenuAnchor(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <MenuItem onClick={handleSaveScheduleClick}>
                      <ListItemIcon>
                        <Save fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Save</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleShareScheduleClick}>
                      <ListItemIcon>
                        <Share fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Share</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleLoadScheduleClick}>
                      <ListItemIcon>
                        <FolderOpen fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Load</ListItemText>
                    </MenuItem>
                  </Menu>
                  </>
                  )}
                  {!isNarrowScheduleHeader && (
                  <Tooltip title="Print Schedule">
                    <IconButton
                      size="small"
                      onClick={handlePrintSchedule}
                      sx={{
                        background: 'primary.main',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px',
                        '&:hover': {
                          background: 'primary.dark',
                        },
                      }}
                    >
                      <Print fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  )}
                  {isNarrowScheduleHeader && (
                    <>
                      <Tooltip title="Schedule actions" disableHoverListener={!!myScheduleActionsAnchor}>
                        <IconButton
                          size="small"
                          onClick={(e) => setMyScheduleActionsAnchor(e.currentTarget)}
                          sx={{
                            background: 'secondary.main',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px',
                            '&:hover': {
                              background: 'secondary.dark',
                            },
                          }}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={myScheduleActionsAnchor}
                        open={!!myScheduleActionsAnchor}
                        onClose={() => setMyScheduleActionsAnchor(null)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            setScheduleLayout('horizontal');
                            setMyScheduleActionsAnchor(null);
                          }}
                        >
                          <ListItemIcon>
                            <ViewColumn fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Horizontal Layout</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setScheduleLayout('vertical');
                            setMyScheduleActionsAnchor(null);
                          }}
                        >
                          <ListItemIcon>
                            <ViewAgenda fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Vertical Layout</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem
                          selected={weekView === 'full'}
                          onClick={() => {
                            setWeekView('full');
                            setMyScheduleActionsAnchor(null);
                          }}
                        >
                          <ListItemIcon>
                            <ViewWeek fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Full week</ListItemText>
                        </MenuItem>
                        <MenuItem
                          selected={weekView === 'twoDay'}
                          onClick={() => {
                            setWeekView('twoDay');
                            setMyScheduleActionsAnchor(null);
                          }}
                        >
                          <ListItemIcon>
                            <Swipe fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>2-day scroll</ListItemText>
                        </MenuItem>
                        {savedSchedules.length >= 2 && !compareMode && (
                          <MenuItem
                            onClick={() => {
                              const anchor = myScheduleActionsAnchor;
                              setMyScheduleActionsAnchor(null);
                              setCompareMenuAnchor(anchor);
                            }}
                          >
                            <ListItemIcon>
                              <CompareArrows fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Compare schedules</ListItemText>
                          </MenuItem>
                        )}
                        {savedSchedules.length > 0 && [
                          <Divider key="saved-divider" />,
                          <ListSubheader key="saved-header" sx={{ lineHeight: '32px', background: 'transparent' }}>
                            Saved schedules
                          </ListSubheader>,
                          <MenuItem
                            key="saved-current"
                            selected={selectedScheduleId === ''}
                            onClick={() => {
                              setSelectedScheduleId('');
                              setMyScheduleActionsAnchor(null);
                            }}
                          >
                            <ListItemText primaryTypographyProps={{ fontStyle: 'italic' }}>Current Schedule</ListItemText>
                          </MenuItem>,
                          ...savedSchedules.map((schedule) => (
                            <MenuItem
                              key={schedule.id}
                              selected={selectedScheduleId === schedule.id}
                              onClick={() => {
                                handleLoadSavedSchedule(schedule.id);
                                setMyScheduleActionsAnchor(null);
                              }}
                            >
                              <ListItemIcon>
                                <FolderOpen fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>{schedule.name}</ListItemText>
                            </MenuItem>
                          )),
                        ]}
                        <Divider />
                        <MenuItem
                          onClick={() => {
                            setMyScheduleActionsAnchor(null);
                            handleSaveScheduleClick();
                          }}
                        >
                          <ListItemIcon>
                            <Save fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Save</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setMyScheduleActionsAnchor(null);
                            handleShareScheduleClick();
                          }}
                        >
                          <ListItemIcon>
                            <Share fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Share</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setMyScheduleActionsAnchor(null);
                            handleLoadScheduleClick();
                          }}
                        >
                          <ListItemIcon>
                            <FolderOpen fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Load</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setMyScheduleActionsAnchor(null);
                            handlePrintSchedule();
                          }}
                        >
                          <ListItemIcon>
                            <Print fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Print</ListItemText>
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>
              </Box>
              <ScheduleGrid
                courses={appState.mySchedule}
                mySchedule={appState.mySchedule}
                customBlocks={appState.customBlocks}
                filters={appState.filters}
                onAddCourse={handleAddCourse}
                onRemoveCourse={handleRemoveCourse}
                onEditCustomBlock={handleEditCustomBlock}
                courseOpacity={courseOpacity}
                isMySchedule={true}
                sharedTimeRange={sharedTimeRange}
                weekView={weekView}
              />
            </Box>
            
            {/* My Online Classes Board */}
            <Box sx={{
              background: (theme) => theme.palette.mode === 'dark' ? '#1a2330' : '#ffffff',
              border: (theme) => theme.palette.mode === 'dark' 
                ? '1px solid #203045' 
                : '1px solid #d1d5db',
              borderRadius: '16px',
              padding: '10px',
              minHeight: '200px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <Typography variant="h6" sx={{
                  margin: '4px 8px 8px 8px',
                  fontSize: '15px',
                  color: 'text.secondary'
                }}>
                  💻 My Online Classes
                </Typography>
              </Box>
              <OnlineCoursesList
                courses={appState.myOnlineClasses}
                myOnlineClasses={appState.myOnlineClasses}
                filters={appState.filters}
                onAddOnlineCourse={handleAddOnlineCourse}
                onRemoveOnlineCourse={handleRemoveOnlineCourse}
              />
            </Box>
          </Box>
          )}
        </Box>
      </Box>

      <Snackbar
        open={Boolean(catalogNotice)}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') setCatalogNotice(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={() => setCatalogNotice(null)} sx={{ maxWidth: 560 }}>
          {catalogNotice}
        </Alert>
      </Snackbar>

      <ShareAppModal open={shareAppOpen} onClose={() => setShareAppOpen(false)} />

      {/* Import Modal */}
        <ImportModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onLoadCatalog={handleLoadCatalog}
          onCompleteImport={(neededCourses) => {
            handleFilterChange(filtersFromNeededCourses(neededCourses, appState.filters.subjectAllow, appState.filters.neededCourses));
            setImportModalOpen(false);
          }}
          isLoading={appState.isLoading}
          error={appState.error}
          progress={appState.importProgress}
          progressText={appState.importProgressText}
          subjects={appState.subjects}
          selectedClasses={appState.filters.neededCourses}
          allCourses={appState.allCourses}
          onlineCourses={appState.onlineCourses}
          subjectData={appState.subjectData}
          scheduleLabel={scheduleLabel}
          lastUpdatedLabel={lastUpdatedLabel}
        />

      {/* Save/Load Modal */}
      <SaveLoadModal
        open={saveLoadModalOpen}
        onClose={() => setSaveLoadModalOpen(false)}
        mySchedule={appState.mySchedule}
        myOnlineClasses={appState.myOnlineClasses}
        customBlocks={appState.customBlocks}
        onLoadSchedule={handleLoadEncodedSchedule}
        initialTab={saveLoadModalTab}
      />

      {/* Save Schedule Modal */}
      <Dialog
        open={saveScheduleModalOpen}
        onClose={() => {
          setSaveScheduleModalOpen(false);
          setScheduleName('');
          setEditingScheduleId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingScheduleId ? 'Edit Schedule' : 'Save Schedule'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Schedule Name"
            fullWidth
            variant="outlined"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSaveSchedule();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSaveScheduleModalOpen(false);
            setScheduleName('');
            setEditingScheduleId(null);
          }}>
            Cancel
          </Button>
          <Button onClick={handleSaveSchedule} variant="contained">
            {editingScheduleId ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Schedule Modal */}
      <Dialog
        open={editScheduleModalOpen}
        onClose={() => {
          setEditScheduleModalOpen(false);
          setScheduleName('');
          setEditingScheduleId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Schedule</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Schedule Name"
            fullWidth
            variant="outlined"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRenameSchedule();
              }
            }}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              color="error"
              startIcon={<Delete />}
              onClick={() => {
                if (editingScheduleId) {
                  handleDeleteSchedule(editingScheduleId);
                  setEditScheduleModalOpen(false);
                  setScheduleName('');
                  setEditingScheduleId(null);
                }
              }}
            >
              Delete Schedule
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditScheduleModalOpen(false);
            setScheduleName('');
            setEditingScheduleId(null);
          }}>
            Cancel
          </Button>
          <Button onClick={handleRenameSchedule} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Block Modal */}
      <CustomBlockModal
        open={customBlockModalOpen}
        onClose={() => {
          setCustomBlockModalOpen(false);
          setEditingCustomBlock(null);
        }}
        onSave={handleSaveCustomBlock}
        onDelete={handleDeleteCustomBlock}
        editingBlock={editingCustomBlock}
      />

      {/* Restore Data Prompt */}
      <Dialog
        open={showRestorePrompt}
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #1a2330 0%, #0f1622 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            border: (theme) => theme.palette.mode === 'dark'
              ? '1px solid #2a3c55'
              : '1px solid #d1d5db',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          },
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center',
          fontSize: '20px',
          fontWeight: 'bold',
          color: 'text.primary',
          padding: '24px 24px 16px 24px'
        }}>
          🔄 Restore Saved Schedule?
        </DialogTitle>
        
        <DialogContent sx={{ padding: '0 24px 24px 24px' }}>
          <Typography variant="body1" sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            lineHeight: 1.6,
            mb: 3
          }}>
            We found a saved schedule from your previous session. Would you like to restore it?
          </Typography>
          
          <Box sx={{ 
            background: (theme) => theme.palette.mode === 'dark' 
              ? 'rgba(138, 180, 248, 0.1)' 
              : 'rgba(37, 99, 235, 0.05)',
            border: (theme) => theme.palette.mode === 'dark'
              ? '1px solid rgba(138, 180, 248, 0.2)'
              : '1px solid rgba(37, 99, 235, 0.1)',
            borderRadius: '12px',
            padding: '16px',
            mb: 3
          }}>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontSize: '13px',
              textAlign: 'center'
            }}>
              This includes your imported courses, selected schedule, filter preferences, and theme settings.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          padding: '0 24px 24px 24px',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <Button
            variant="outlined"
            onClick={handleDiscardData}
            sx={{
              borderColor: (theme) => theme.palette.mode === 'dark' ? '#2a3c55' : '#d1d5db',
              color: 'text.secondary',
              textTransform: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              '&:hover': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#3a4a5c' : '#9ca3af',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1a2532' : '#f3f4f6',
              }
            }}
          >
            Start Fresh
          </Button>
          <Button
            variant="contained"
            onClick={handleRestoreData}
            sx={{
              background: 'linear-gradient(135deg, #2563eb, #059669)',
              color: 'white',
              textTransform: 'none',
              borderRadius: '8px',
              padding: '8px 24px',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8, #047857)',
              }
            }}
          >
            Restore Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default App;
